import jsonExport from "jsonexport/dist";
import { downloadCSV, useGetIdentity, useListContext, useRecordContext, useUpdate, type Exporter } from "ra-core";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { ExportButton } from "@/components/admin/export-button";
import { UrlField } from "@/components/admin/url-field";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TopToolbar } from "../layout/TopToolbar";

const exporter: Exporter = (applications) => {
  return jsonExport(applications, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "applications");
  });
};

const STATUS_OPTIONS = [
  { value: "pending",  label: "Pending",  variant: "warning" },
  { value: "accepted", label: "Accepted", variant: "success" },
  { value: "rejected", label: "Rejected", variant: "destructive" },
] as const;
const ApplicationStatusSelect = () => {
  const record = useRecordContext();
  const [update, { isPending }] = useUpdate();
  if (!record) return null;

  const current = STATUS_OPTIONS.find((s) => s.value === record.status) ?? STATUS_OPTIONS[0];

  const handleChange = (value: string) => {
    update("applications", {
      id: record.id,
      data: { status: value },
      previousData: record,
    });
  };

  return (
    <Select value={record.status} onValueChange={handleChange} disabled={isPending}>
      <SelectTrigger className="w-32 h-7 border-0 shadow-none p-0 focus:ring-0">
        <SelectValue>
          <Badge variant={current.variant}>{current.label}</Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            <Badge variant={s.variant}>{s.label}</Badge>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const ApplicationListLayout = () => {
  const { isPending } = useListContext();
  if (isPending) return null;
  return (
    <DataTable bulkActionButtons={false}>
      <DataTable.Col source="name" label="Name" />
      <DataTable.Col source="email" label="Email" />
      <DataTable.Col source="project_name" label="Project" />
      <DataTable.Col source="archetype" label="Archetype" />
      <DataTable.Col source="functions" label="Functions" />
      <DataTable.Col source="status" label="Status">
        <ApplicationStatusSelect />
      </DataTable.Col>
      <DataTable.Col source="portfolio_url" label="Portfolio">
        <UrlField source="portfolio_url" target="_blank" />
      </DataTable.Col>
      <DataTable.Col source="description" label="Description" cellClassName="max-w-xs truncate" />
      <DataTable.Col source="created_at" label="Submitted">
        <DateField source="created_at" showTime />
      </DataTable.Col>
    </DataTable>
  );
};

const ApplicationListActions = () => (
  <TopToolbar>
    <SortButton fields={["created_at", "name", "status"]} />
    <ExportButton exporter={exporter} />
  </TopToolbar>
);

export const ApplicationList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      sort={{ field: "created_at", order: "DESC" }}
      perPage={25}
      actions={<ApplicationListActions />}
      exporter={exporter}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <ApplicationListLayout />
    </List>
  );
};
